import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";
import { CATEGORIES, formatCategoryLabel } from "../../constants/categories";

// The admin sidebar's "Update platter" link used to point at the
// Item-shaped UpdateItem component, which fetches /menu/get-items and
// posts an Item-shaped body - platters have a completely different shape
// (platterType + a foodItems array) and their own API, so that route
// never actually worked. This is the real, platter-shaped replacement.
const UpdatePlatter = () => {
  const [platters, setPlatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatter, setSelectedPlatter] = useState(null);
  const [platterTypeInput, setPlatterTypeInput] = useState("");
  const [newItem, setNewItem] = useState({
    title: "",
    desc: "",
    price: "",
    category: "",
    imageUrl: "",
    availability: true,
  });
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  const fetchPlatters = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/custom/platters`);
      setPlatters(response.data);
    } catch (err) {
      console.error("Error fetching platters:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatters();
  }, []);

  const openPlatter = (platter) => {
    setError(null);
    setSelectedPlatter(platter);
    setPlatterTypeInput(platter.platterType);
  };

  const closePlatter = () => {
    setSelectedPlatter(null);
    setNewItem({ title: "", desc: "", price: "", category: "", imageUrl: "", availability: true });
  };

  const refreshSelected = async (platterId) => {
    const response = await axios.get(`${API_BASE_URL}/custom/platters/${platterId}`);
    setSelectedPlatter(response.data);
    await fetchPlatters();
  };

  const handleRename = async (e) => {
    e.preventDefault();
    setError(null);
    if (!platterTypeInput.trim()) {
      setError("Platter name can't be empty.");
      return;
    }
    try {
      await axios.put(
        `${API_BASE_URL}/custom/platters/${selectedPlatter._id}`,
        { platterType: platterTypeInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshSelected(selectedPlatter._id);
    } catch (err) {
      console.error("Error renaming platter:", err);
      setError(err.response?.data?.message || "Failed to rename platter.");
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setError(null);

    const price = Number(newItem.price);
    if (!newItem.title.trim() || !newItem.desc.trim() || !newItem.category || !newItem.imageUrl) {
      setError("Fill in title, description, category, and image before adding.");
      return;
    }
    if (Number.isNaN(price) || price <= 0) {
      setError("Price must be a positive number.");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/custom/platters/${selectedPlatter._id}/items`,
        { ...newItem, price },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewItem({ title: "", desc: "", price: "", category: "", imageUrl: "", availability: true });
      await refreshSelected(selectedPlatter._id);
    } catch (err) {
      console.error("Error adding item to platter:", err);
      setError(err.response?.data?.message || "Failed to add item.");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Remove this item from the platter?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/custom/platters/${selectedPlatter._id}/items/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshSelected(selectedPlatter._id);
    } catch (err) {
      console.error("Error removing item from platter:", err);
      alert(err.response?.data?.message || "Failed to remove item.");
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await axios.put(
        `${API_BASE_URL}/custom/platters/${selectedPlatter._id}/items/${item._id}`,
        { availability: !item.availability },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshSelected(selectedPlatter._id);
    } catch (err) {
      console.error("Error updating item availability:", err);
      alert(err.response?.data?.message || "Failed to update item.");
    }
  };

  const handleDeletePlatter = async (platterId) => {
    if (!window.confirm("Delete this entire platter? This can't be undone.")) return;
    try {
      await axios.delete(`${API_BASE_URL}/custom/platters/${platterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatters(platters.filter((p) => p._id !== platterId));
      if (selectedPlatter?._id === platterId) closePlatter();
    } catch (err) {
      console.error("Error deleting platter:", err);
      alert(err.response?.data?.message || "Failed to delete platter.");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading platters…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Custom Platters</h2>

      {platters.length === 0 ? (
        <p className="text-sub">No platters yet - create one first.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {platters.map((platter) => (
            <div key={platter._id} className="bg-card border border-line rounded-lg p-4">
              <h3 className="text-lg font-semibold text-ink">{platter.platterType}</h3>
              <p className="text-sm text-sub">{platter.foodItems.length} item(s)</p>
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => openPlatter(platter)}
                  className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition"
                >
                  Manage
                </button>
                <button
                  onClick={() => handleDeletePlatter(platter._id)}
                  className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedPlatter && (
        <div className="fixed inset-0 bg-ink/60 flex justify-center items-center z-10 p-4 overflow-y-auto">
          <div className="bg-card p-6 rounded-lg border border-line w-full max-w-lg my-8">
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-4">
                {error}
              </p>
            )}

            <form onSubmit={handleRename} className="flex gap-2 mb-6">
              <input
                value={platterTypeInput}
                onChange={(e) => setPlatterTypeInput(e.target.value)}
                className="flex-1 p-2 border border-line rounded"
              />
              <button
                type="submit"
                className="bg-primary text-white px-4 rounded font-semibold hover:opacity-90 transition"
              >
                Rename
              </button>
            </form>

            <h3 className="text-sm font-semibold text-ink mb-2">Items in this platter</h3>
            {selectedPlatter.foodItems.length === 0 ? (
              <p className="text-sm text-sub mb-4">No items yet.</p>
            ) : (
              <ul className="space-y-2 mb-6">
                {selectedPlatter.foodItems.map((item) => (
                  <li
                    key={item._id}
                    className="border border-line rounded p-2 flex items-center gap-3"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-12 h-12 object-cover rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink truncate">{item.title}</p>
                      <p className="text-xs text-sub">
                        ৳{item.price} · {formatCategoryLabel(item.category)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggleAvailability(item)}
                      className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                        item.availability
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {item.availability ? "Available" : "Unavailable"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item._id)}
                      className="text-red-600 text-xs font-semibold shrink-0"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="text-sm font-semibold text-ink mb-2">Add an item</h3>
            <form onSubmit={handleAddItem} className="space-y-2">
              <input
                placeholder="Title"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="w-full p-2 border border-line rounded"
              />
              <input
                placeholder="Description"
                value={newItem.desc}
                onChange={(e) => setNewItem({ ...newItem, desc: e.target.value })}
                className="w-full p-2 border border-line rounded"
              />
              <input
                type="number"
                min="0"
                step="1"
                placeholder="Price (৳)"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                className="w-full p-2 border border-line rounded"
              />
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="w-full p-2 border border-line rounded"
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {formatCategoryLabel(cat)}
                  </option>
                ))}
              </select>
              <ImageUpload
                label="Image"
                value={newItem.imageUrl}
                onChange={(imageUrl) => setNewItem({ ...newItem, imageUrl })}
              />
              <button
                type="submit"
                className="w-full border border-primary text-primary p-2 rounded-lg font-bold hover:bg-primary/10 transition"
              >
                Add Item
              </button>
            </form>

            <button
              type="button"
              onClick={closePlatter}
              className="w-full mt-4 border border-line text-ink py-2 rounded hover:bg-paper transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePlatter;
